import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import LoaderLayout from 'components/Layouts/LoaderLayout';
import AccountContext, { AccountContextInterface } from 'storage/AccountContext';
import ProfileInfoContext from 'storage/ProfileInfoContext';
import './accountSelector.scss';

function AccountSelector<T extends AccountContextInterface['accountsList'][0]>({
  accountsList,
  isFetching,
  onChange,
  onChangeAndConfirm,
  selected,
}: {
  accountsList: T[];
  isFetching: boolean;
  selected: T;
  onChange: (account: T) => void;
  onChangeAndConfirm: (account: T) => void;
}) {
  const { profileInfo } = ProfileInfoContext.useContext();

  const onChangeAccount = useCallback(
    (event, newValue) => {
      if (newValue) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const isAdmin = profileInfo?.admin || false;

  const { activeAccounts, inactiveAccounts, onHoldAccounts } = useMemo(() => {
    return accountsList.reduce(
      (acc, item) => {
        switch (item.status) {
          case 0: // ACTIVE
            acc.activeAccounts.push(item);
            break;
          case 1: // ON_HOLD
            if (isAdmin) {
              acc.onHoldAccounts.push(item);
            } else {
              // For non-admin users, merge on hold accounts into active
              acc.activeAccounts.push(item);
            }
            break;
          case 3: // DELETED
            break;
          case 2: // INACTIVE
          default:
            acc.inactiveAccounts.push(item);
            break;
        }
        return acc;
      },
      {
        activeAccounts: [] as T[],
        onHoldAccounts: [] as T[],
        inactiveAccounts: [] as T[],
      },
    );
  }, [accountsList, isAdmin]);

  const hasActiveAccounts = activeAccounts.length > 0;
  const hasOnHoldAccounts = onHoldAccounts.length > 0;
  const hasInactiveAccounts = inactiveAccounts.length > 0;

  if (accountsList.length === 0) {
    return <p className="mt_1em">There are no accounts associated with the current user.</p>;
  }

  return (
    <LoaderLayout className="accountSelector" isLoading={isFetching}>
      {hasActiveAccounts ? (
        <>
          <h4 className="mt_1em">Active Accounts</h4>
          <Autocomplete
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={onChangeAccount}
            options={activeAccounts}
            renderInput={params => <TextField {...params} placeholder="Search active accounts..." variant="outlined" />}
            value={selected && activeAccounts.some(x => x.id === selected.id) ? selected : null}
          />
        </>
      ) : (
        <p className="mt_1em">There are no active accounts associated with the current user.</p>
      )}
      {isAdmin && hasOnHoldAccounts && (
        <>
          <h4 className="mt_1em">On Hold Accounts</h4>
          <Autocomplete
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={onChangeAccount}
            options={onHoldAccounts}
            renderInput={params => (
              <TextField {...params} placeholder="Search on hold accounts..." variant="outlined" />
            )}
            value={selected && onHoldAccounts.some(x => x.id === selected.id) ? selected : null}
          />
        </>
      )}
      {hasInactiveAccounts && (
        <>
          <h4 className="mt_1em">Inactive Accounts</h4>
          <Autocomplete
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={onChangeAccount}
            options={inactiveAccounts}
            renderInput={params => (
              <TextField {...params} placeholder="Search inactive accounts..." variant="outlined" />
            )}
            value={
              selected &&
              !activeAccounts.some(x => x.id === selected.id) &&
              !onHoldAccounts.some(x => x.id === selected.id)
                ? selected
                : null
            }
          />
        </>
      )}
    </LoaderLayout>
  );
}

export default AccountContext.withContext(['accountsList', 'isFetching'])(AccountSelector);
